/**
 * הזמנות ייצור ורכש — the work order we place with a factory or a supplier.
 *
 * ## Where this sits
 *
 * תכנון ופיתוח (`productPlanning.*`) ends when a project is *decided*: the kit's components are
 * listed, each one has a chosen quote from a chosen supplier, and the artwork is marked סופי. This
 * module is the step after — one row that says "make 10,000 of these, at this price, by this date"
 * and a document that goes out to the supplier.
 *
 * ## Why the order snapshots the project instead of pointing at it
 *
 * Every field the document prints — the item name, its SKU, its barcode, the quantity, the unit
 * price — is copied onto `ProductionOrderItems` at creation. A live join to `PlanningProjectItems`
 * and `PlanningQuotes` would make a document that has already been sent to a factory change when
 * somebody edits the planning card, and "what did we order" would have no answer. The planning link
 * (`planningProjectId`, `planningItemId`) is kept for provenance, not for reads.
 *
 * **The files are the deliberate exception.** They are stored as pointers to
 * `PlanningFileVersions`, because the point of the attachment is that the factory has the *current*
 * artwork. A revision that loses its "קובץ סופי" mark leaves the order with a stale attachment, and
 * the order is flagged rather than silently changed — see `PRODUCTION_ORDER_FILE_STALE_NOTE`.
 *
 * ## Why the statuses are permissive
 *
 * The status column records what *the factory* is doing, and we learn it by phone and by email.
 * A purchaser who is told that production started must be able to say so, even if nobody pressed
 * "אושרה ע״י היצרן" first — so any live status may follow any other. The one hard gate is on
 * *sending* (`canSendProductionOrder`), because that is the step with an outside consequence.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly.
 */

import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

/**
 * The seven steps of the flow, in order, plus `cancelled` outside it.
 *
 * Codes are stable English; labels are the requester's Hebrew from the approved mockup. The DB
 * column is a `STRING` (repo convention), so adding a step is one entry here and never an
 * `ALTER TYPE`.
 */
export const PRODUCTION_ORDER_FLOW = [
  'draft',
  'sent',
  'approved',
  'in_production',
  'quality_check',
  'ready_to_ship',
  'shipped'
] as const;
export type ProductionOrderFlowStatus = (typeof PRODUCTION_ORDER_FLOW)[number];

export const PRODUCTION_ORDER_STATUSES = [...PRODUCTION_ORDER_FLOW, 'cancelled'] as const;
export type ProductionOrderStatus = (typeof PRODUCTION_ORDER_STATUSES)[number];

export const PRODUCTION_ORDER_STATUS_LABELS: Record<ProductionOrderStatus, string> = {
  draft: 'טיוטה',
  sent: 'נשלחה ליצרן',
  approved: 'אושרה ע״י היצרן',
  in_production: 'בייצור',
  quality_check: 'בבקרת איכות',
  ready_to_ship: 'מוכן למשלוח',
  shipped: 'נשלח',
  cancelled: 'מבוטלת'
};

export const PRODUCTION_ORDER_STATUS_OPTIONS: PhOption<ProductionOrderStatus>[] = phOptions(
  PRODUCTION_ORDER_STATUSES,
  PRODUCTION_ORDER_STATUS_LABELS
);

/** The status menu on a row and on the card: the flow, without `cancelled`, which is its own action. */
export const PRODUCTION_ORDER_FLOW_OPTIONS: PhOption<ProductionOrderFlowStatus>[] = phOptions(
  PRODUCTION_ORDER_FLOW,
  PRODUCTION_ORDER_STATUS_LABELS
);

/**
 * `cancelled` is the only terminal status, and the only status a *live* order cannot be moved back
 * from: un-cancelling an order somebody has been told not to make is a decision with money behind
 * it, and is done by opening a new order rather than by reviving this one.
 *
 * Everything else is reachable from everything else — see the module comment.
 */
export const canTransitionProductionOrderStatus = (from: ProductionOrderStatus, to: ProductionOrderStatus): boolean =>
  from === to || (from !== 'cancelled' && PRODUCTION_ORDER_STATUSES.includes(to));

export const productionOrderTransitionRefusal = (from: ProductionOrderStatus, to: ProductionOrderStatus): string =>
  `לא ניתן לשנות סטטוס מ"${PRODUCTION_ORDER_STATUS_LABELS[from]}" ל"${PRODUCTION_ORDER_STATUS_LABELS[to]}" — הזמנה מבוטלת אינה חוזרת לתהליך`;

/** A draft has not been seen by anybody outside the office, so it is the one status that may be deleted. */
export const isProductionOrderDeletable = (status: ProductionOrderStatus): boolean => status === 'draft';

/** An order still in the factory's hands. What the KPI row counts as "פעילות". */
export const isProductionOrderActive = (status: ProductionOrderStatus): boolean =>
  status !== 'cancelled' && status !== 'shipped';

/* ------------------------------------------------------------------ */
/* The list screen's KPI row                                           */
/* ------------------------------------------------------------------ */

/**
 * The six cards above the table, in the mockup's order. Each one is a filter, not a statistic —
 * clicking it sets `status` on the query — which is why the definition carries the status it
 * filters by rather than a count: the count comes from the API.
 *
 * `all` is the odd one out and deliberately so: it clears the filter.
 */
export type ProductionOrderKpiKey = 'all' | ProductionOrderStatus;

export interface ProductionOrderKpiDef {
  key: ProductionOrderKpiKey;
  label: string;
  note: string;
}

// Typed by annotation rather than `as const satisfies` — the repo is on TypeScript 4.8, which does
// not have `satisfies`. The annotation gives the same guarantee that a typo in a key is a build
// error; what it gives up is the literal union, which `ProductionOrderKpiKey` states directly.
export const PRODUCTION_ORDER_KPIS: ProductionOrderKpiDef[] = [
  { key: 'all', label: 'סה״כ פעילות', note: 'כל ההזמנות' },
  { key: 'sent', label: 'ממתין ליצרן', note: 'טרם אושרו' },
  { key: 'in_production', label: 'בייצור', note: 'בקו הייצור' },
  { key: 'quality_check', label: 'בבקרת איכות', note: 'לפני שחרור' },
  { key: 'ready_to_ship', label: 'מוכן למשלוח', note: 'ממתין לשילוח' },
  { key: 'draft', label: 'טיוטות', note: 'טרם נשלחו' }
];

/** `{ draft: 2, in_production: 1, ... }` — every status, including the ones at zero. */
export type ProductionOrderStatusCounts = Record<ProductionOrderStatus, number>;

/* ------------------------------------------------------------------ */
/* Commercial terms                                                    */
/* ------------------------------------------------------------------ */

/**
 * Incoterm as a code plus a place, and not one free-text field, because the document prints it in
 * two languages: `FOB אשדוד` on the Hebrew sheet and `FOB Ashdod` on the English one. One string
 * would print the Hebrew place on the English document, which is exactly the line a customs broker
 * reads.
 */
export const INCOTERMS = ['fob', 'exw', 'cif', 'ddp', 'dap', 'other'] as const;
export type Incoterm = (typeof INCOTERMS)[number];

export const INCOTERM_LABELS: Record<Incoterm, string> = {
  fob: 'FOB',
  exw: 'EXW',
  cif: 'CIF',
  ddp: 'DDP',
  dap: 'DAP',
  other: 'אחר'
};
export const INCOTERM_OPTIONS: PhOption<Incoterm>[] = phOptions(INCOTERMS, INCOTERM_LABELS);

/** `fob` + `אשדוד` → `FOB אשדוד`. `other` prints the place alone, which is what "אחר" means. */
export const formatIncoterm = (incoterm: Incoterm | null, place: string | null): string => {
  const code = incoterm && incoterm !== 'other' ? INCOTERM_LABELS[incoterm] : '';
  return [code, place?.trim()].filter(Boolean).join(' ');
};

/**
 * The barcode the factory prints on the packaging. One type per order — the mockup's dropdown —
 * and the value itself per item, because each component carries its own.
 */
export const BARCODE_TYPES = ['EAN-13', 'UPC-A', 'Code 128', 'QR'] as const;
export type BarcodeType = (typeof BARCODE_TYPES)[number];
export const BARCODE_TYPE_OPTIONS: PhOption<BarcodeType>[] = BARCODE_TYPES.map(value => ({
  value,
  label: value
}));

/* ------------------------------------------------------------------ */
/* Files                                                               */
/* ------------------------------------------------------------------ */

/**
 * Where an attachment came from. `planning` is a pointer into `PlanningFileVersions`; `upload` is a
 * file somebody added on this screen and that the planning card never saw.
 *
 * The distinction is not cosmetic: only a `planning` row can go stale, and only an `upload` row is
 * deleted outright when it is removed.
 */
export const PRODUCTION_ORDER_FILE_SOURCES = ['planning', 'upload'] as const;
export type ProductionOrderFileSource = (typeof PRODUCTION_ORDER_FILE_SOURCES)[number];

export const PRODUCTION_ORDER_FILE_SOURCE_LABELS: Record<ProductionOrderFileSource, string> = {
  planning: 'מכרטיס התכנון',
  upload: 'הועלה להזמנה'
};

/** The mockup's own wording, kept in one place because both the drawer and the card print it. */
export const PRODUCTION_ORDER_FILES_NOTE =
  'כל הקבצים שסומנו כסופיים בכרטיס התכנון מצורפים אוטומטית. אפשר להסיר, לצרף קבצים נוספים מכרטיס המוצר או להעלות קובץ חיצוני.';

export const PRODUCTION_ORDER_FILE_STALE_NOTE =
  'הקבצים נשלחים ליצרן יחד עם ההזמנה. קובץ שסימונו הוסר בכרטיס הפיתוח יורד מכאן אוטומטית וההזמנה תסומן כדורשת עדכון.';

export const PRODUCTION_ORDER_NO_FILES_WARNING = 'לא צורפו קבצים — היצרן לא יקבל מפרט או Artwork.';

/* ------------------------------------------------------------------ */
/* The order log                                                       */
/* ------------------------------------------------------------------ */

/**
 * יומן הזמנה. `system` entries are written by the code; `note` is the only kind a person types.
 *
 * The body is rendered from the kind and a payload rather than stored as a sentence, so that a
 * change of wording applies to the whole history — the planning module's `systemMessage.service.ts`
 * learnt this the same way.
 */
export const PRODUCTION_ORDER_EVENT_KINDS = [
  'created',
  'status_changed',
  'sent_to_supplier',
  'send_failed',
  'updated',
  'file_attached',
  'file_detached',
  // Written by שילוח ספקים: a shipment that carries this order's lines was opened or moved.
  'shipment_linked',
  'shipment_status_changed',
  'note'
] as const;
export type ProductionOrderEventKind = (typeof PRODUCTION_ORDER_EVENT_KINDS)[number];

/* ------------------------------------------------------------------ */
/* The document                                                        */
/* ------------------------------------------------------------------ */

/** The two sheets the order produces. Both are generated from the same row. */
export const PRODUCTION_ORDER_DOC_LANGS = ['he', 'en'] as const;
export type ProductionOrderDocLang = (typeof PRODUCTION_ORDER_DOC_LANGS)[number];

export const PRODUCTION_ORDER_DOC_LANG_LABELS: Record<ProductionOrderDocLang, string> = {
  he: 'עברית',
  en: 'English'
};

/* ------------------------------------------------------------------ */
/* DTOs — list                                                         */
/* ------------------------------------------------------------------ */

/** One row of טבלת מעקב הזמנות. Everything the table prints, and nothing it does not. */
export interface ProductionOrderRowDto {
  id: number;
  /** `PO-2041`. Generated, unique, and what every human uses to name the order. */
  code: string;
  /** The project's name, snapshotted — see the module comment. */
  productName: string;
  planningProjectId: number | null;
  /** The project's own code (`DEV-00241`), for the "מתוך פרויקט" line. */
  planningProjectCode: string | null;
  supplierId: number | null;
  supplierName: string;
  /** Sum of the items' quantities — the mockup's "כמות" column. */
  totalQuantity: number;
  itemCount: number;
  status: ProductionOrderStatus;
  /** `YYYY-MM-DD`, or null for a draft that has not been given a date. The column shows "—". */
  dueDate: string | null;
  /** Sum of `quantity * unitPrice`, as a decimal string — never a float. */
  totalCost: string;
  currency: string;
  updatedAt: string;
  /** True when any attached planning file is no longer marked סופי. Draws the "דורשת עדכון" flag. */
  hasStaleFiles: boolean;
}

export interface ProductionOrderListDto {
  rows: ProductionOrderRowDto[];
  /** Rows matching the filter, for the pager. `{ rows, count }` is the repo's list shape. */
  count: number;
  /** Counts for the KPI row, computed over **every** order so the cards do not move as you filter. */
  counts: ProductionOrderStatusCounts;
  /** Active orders — what the `all` card shows. */
  activeCount: number;
}

/* ------------------------------------------------------------------ */
/* DTOs — the card                                                     */
/* ------------------------------------------------------------------ */

export interface ProductionOrderItemDto {
  id: number;
  planningItemId: number | null;
  /** The kit the line comes from, and its code — printed beside a line from another kit than the order's. */
  planningProjectId: number | null;
  planningProjectCode: string | null;
  name: string;
  /** The English name printed on the English document. Falls back to `name` when nobody typed one. */
  nameEn: string | null;
  sku: string | null;
  barcode: string | null;
  quantity: number;
  /** Decimal string. `quantity * unitPrice` is computed, never stored. */
  unitPrice: string;
  currency: string;
  lineTotal: string;
  sortOrder: number;
}

/**
 * An attachment as the card and the drawer print it.
 *
 * The meta line ("שרה פרידמן · 08.09 · PDF 11MB") is composed on the client from these parts and
 * not by the serializer, because `formatFileSize` and the date formatters live on the frontend and
 * the planning file library already renders the same line from the same pieces.
 */
export interface ProductionOrderFileDto {
  id: number;
  source: ProductionOrderFileSource;
  /** The name as attached — the file group's name in planning, the uploaded name otherwise. */
  name: string;
  fileName: string;
  fileSize: number | null;
  mimeType: string | null;
  /** The address the download link points at. See `googleStorage.ts` on why this is enough here. */
  fileUrl: string;
  uploadedByName: string | null;
  uploadedAt: string;
  documentAssetId: number;
  /** Only for `planning` rows: the revision this attachment froze on. */
  planningFileVersionId: number | null;
  revision: number | null;
  /** `planning` row whose revision is no longer the group's סופי. Drawn in red on the card. */
  isStale: boolean;
}

export interface ProductionOrderEventDto {
  id: number;
  kind: ProductionOrderEventKind;
  /** Rendered Hebrew sentence. */
  body: string;
  /** `System` for generated entries, the user's name otherwise. */
  authorName: string;
  createdAt: string;
}

/** One step of the card's milestone rail. */
export interface ProductionOrderMilestoneDto {
  status: ProductionOrderFlowStatus;
  label: string;
  done: boolean;
  current: boolean;
  /** When the order reached this step, if it ever did. */
  at: string | null;
}

export interface ProductionOrderDto extends ProductionOrderRowDto {
  nameEn: string | null;
  supplierContactName: string | null;
  supplierEmail: string | null;
  supplierPhone: string | null;
  supplierCountry: string | null;
  leadTimeDays: number | null;
  incoterm: Incoterm | null;
  incotermPlace: string | null;
  /** Free text: every supplier words its own terms. Printed on the document. */
  paymentTerms: string | null;
  shipToAddress: string | null;
  packingNote: string | null;
  barcodeType: BarcodeType | null;
  instructions: string | null;
  productionCustomerId: number | null;
  productionCustomerName: string | null;
  sentAt: string | null;
  createdAt: string;
  createdByName: string | null;
  items: ProductionOrderItemDto[];
  files: ProductionOrderFileDto[];
  events: ProductionOrderEventDto[];
  milestones: ProductionOrderMilestoneDto[];
}

/* ------------------------------------------------------------------ */
/* DTOs — creating one                                                 */
/* ------------------------------------------------------------------ */

/**
 * What the picker lists: the projects an order may be opened over.
 *
 * The requester was explicit that the drawer must not assume a project — "צור הזמנה למוצר" opens
 * the whole list and you choose. `isReady` is shown rather than used to filter, because a draft may
 * be built over any project; only sending is gated (`canSendProductionOrder`).
 *
 * A project whose demanded items are all already on a non-cancelled production order is **not** in
 * this list. `coveredItemCount` / `demandedItemCount` describe a partial kit ("X מתוך Y פריטים
 * בייצור"). `demandedItemCount` follows the shared coverage rule and is not always `itemCount`.
 */
export interface ProductionOrderProjectOptionDto {
  id: number;
  code: string;
  name: string;
  stageLabel: string;
  productionCustomerName: string | null;
  itemCount: number;
  /** `readyAt` is set — the planning module's own "מוכן לייצור" gate. */
  isReady: boolean;
  /** How many orders already exist over this project. A second round of what is left is legal. */
  orderCount: number;
  /** Items the coverage rule demands. Zero only when the project has nothing to cover. */
  demandedItemCount: number;
  /** How many of those already sit on a non-cancelled production order. Below `demandedItemCount` on a listed row. */
  coveredItemCount: number;
}

/**
 * The drawer's prefill: everything read off the planning card before anybody types.
 *
 * It is a `GET` and not part of the `POST` body on purpose — the drawer has to *show* the pulled
 * values, and the user edits them before creating. Every field here is a suggestion.
 */
export interface ProductionOrderDraftDto {
  planningProjectId: number;
  planningProjectCode: string;
  productName: string;
  nameEn: string | null;
  currency: string;
  dueDate: string | null;
  productionCustomerId: number | null;
  productionCustomerName: string | null;
  /** The project's readiness gate, so the drawer can explain why שליחה is disabled. */
  isReady: boolean;
  readinessFailures: string[];
  items: ProductionOrderDraftItemDto[];
  /** Marked סופי on the planning card: attached by default, `isAttached: true`. */
  files: ProductionOrderDraftFileDto[];
  /** The supplier most of the items point at — the drawer's opening guess for "יצרן". */
  suggestedSupplierId: number | null;
  /** Where the goods are to be delivered: the default warehouse's address, editable. */
  shipToAddress: string | null;
}

export interface ProductionOrderDraftItemDto {
  /** Null for the main product — the project itself, always the first line. */
  planningItemId: number | null;
  /** The kit the line comes from — the drafted project. */
  planningProjectId: number;
  name: string;
  sku: string | null;
  barcode: string | null;
  /** `quantityToProduce`, or the project's `plannedQuantity` when the item has none. */
  quantity: number | null;
  /** From the item's selected quote. Null is "טרם נבחר ספק" and shows as a warning in the drawer. */
  unitPrice: string | null;
  /**
   * The only open offer arrived on the public link and has not been approved.
   *
   * The line stays unpriced. The drawer says it is waiting for approval, which is not the same
   * as a line nobody has quoted.
   */
  needsApproval: boolean;
  currency: string | null;
  supplierId: number | null;
  supplierName: string | null;
  /**
   * Unchecked items are not ordered. A priced line starts checked, unless it is already on a
   * production order — then it stays unchecked so it is not ordered twice.
   */
  isSelected: boolean;
  /** The line already appears on a non-cancelled production order of this project. */
  alreadyInProduction: boolean;
  /** `PO-…` of those orders. Empty when `alreadyInProduction` is false, or when the order has no code. */
  productionOrderCodes: string[];
}

export interface ProductionOrderDraftFileDto {
  planningFileVersionId: number;
  documentAssetId: number;
  name: string;
  fileName: string;
  fileSize: number | null;
  mimeType: string | null;
  uploadedByName: string | null;
  uploadedAt: string;
  revision: number;
  /** סופי files start attached; the rest are offered under "קבצים נוספים בכרטיס המוצר". */
  isFinal: boolean;
  isAttached: boolean;
}

export interface CreateProductionOrderItemBody {
  planningItemId?: number | null;
  /**
   * The kit the line comes from. Omitted means the order's own project. Another kit is allowed only
   * for an item its supplier quoted — the server checks.
   */
  planningProjectId?: number | null;
  name: string;
  nameEn?: string | null;
  sku?: string | null;
  barcode?: string | null;
  quantity: number;
  unitPrice: string | number;
}

export interface CreateProductionOrderBody {
  planningProjectId: number;
  supplierId: number;
  name?: string;
  nameEn?: string | null;
  currency?: string;
  dueDate?: string | null;
  supplierContactName?: string | null;
  supplierEmail?: string | null;
  leadTimeDays?: number | null;
  incoterm?: Incoterm | null;
  incotermPlace?: string | null;
  paymentTerms?: string | null;
  shipToAddress?: string | null;
  packingNote?: string | null;
  barcodeType?: BarcodeType | null;
  instructions?: string | null;
  items: CreateProductionOrderItemBody[];
  /** `PlanningFileVersions.id` — the סופי files the drawer left attached, plus any it added. */
  planningFileVersionIds?: number[];
  /** `draft` saves it; `sent` creates it *and* mails the supplier. Nothing else is accepted. */
  intent: 'draft' | 'send';
}

export type UpdateProductionOrderBody = Partial<
  Omit<CreateProductionOrderBody, 'planningProjectId' | 'intent' | 'items' | 'planningFileVersionIds'>
> & { items?: CreateProductionOrderItemBody[] };

/* ------------------------------------------------------------------ */
/* Sending                                                             */
/* ------------------------------------------------------------------ */

/**
 * The send gate.
 *
 * **Readiness does not block the send.** The planning module's "מוכן לייצור" used to be a hard
 * gate here; the requester lifted it — a purchaser may send before every planning check passes,
 * and the drawer still lists the failing checks as a warning. What the gate keeps is what the
 * factory cannot act without: somebody to send to, something to make (quantity is enforced at
 * `min(1)` by the schema), and a price on every line, since the document the factory receives is
 * a priced purchase order.
 */
export const canSendProductionOrder = (input: {
  status: ProductionOrderStatus;
  hasSupplierEmail: boolean;
  itemCount: number;
  hasUnpricedItem: boolean;
}): { ok: boolean; reason?: string } => {
  if (input.status === 'cancelled') return { ok: false, reason: 'ההזמנה בוטלה' };
  if (!input.itemCount) return { ok: false, reason: 'אין פריטים בהזמנה' };
  if (!input.hasSupplierEmail) return { ok: false, reason: 'לא הוגדר אימייל לשליחת ההזמנה' };
  if (input.hasUnpricedItem) return { ok: false, reason: 'לכל פריט בהזמנה צריך להיות מחיר' };
  return { ok: true };
};

/** The subject line of the mail to the supplier. English, because the recipient is often abroad. */
export const productionOrderMailSubject = (code: string, productName: string): string =>
  `Purchase Order ${code} — ${productName}`;

export interface SendProductionOrderResultDto {
  ok: boolean;
  /** Smoove's campaign id, kept so a send can be traced from their UI. */
  providerMessageId: string | null;
  /** The addresses the order actually went to. */
  recipients: string[];
  /** How many files rode along, the document included. */
  attachmentCount: number;
  error: string | null;
}

/* ------------------------------------------------------------------ */
/* Query                                                               */
/* ------------------------------------------------------------------ */

/**
 * Sortable columns. `dueDate` and `code` are the two the table's headers offer; `updatedAt` is the
 * default, because the screen is a work queue and the thing that moved last is the thing to look at.
 *
 * `totalCost` is deliberately **not** here: it is `SUM(quantity * unitPrice)` over a child table
 * and not a column, so ordering by it would need a join the paging query does not have.
 */
export const PRODUCTION_ORDER_SORT_FIELDS = ['updatedAt', 'createdAt', 'code', 'dueDate', 'status'] as const;
export type ProductionOrderSortField = (typeof PRODUCTION_ORDER_SORT_FIELDS)[number];

/** The query string the list endpoint accepts. Param names follow the repo's `PageQuery`. */
export interface ProductionOrderListQuery {
  page?: number | string;
  rowsPerPage?: number | string;
  sortField?: ProductionOrderSortField;
  sortOrder?: 'asc' | 'desc';
  /** Comma-joined, because the mockup's status filter is a multiSelect. */
  status?: string;
  /** Comma-joined ids — the mockup's supplier filter is single-select, the API is not. */
  supplierId?: string;
  planningProjectId?: number | string;
  productionCustomerId?: number | string;
  /** The planning project's category — an order has none of its own. */
  categoryId?: number | string;
  /** A tag of the planning project the order was opened over. */
  tagId?: number | string;
  /** Matches the order code, the product name and the supplier's name. */
  searchTerm?: string;
}

/* ------------------------------------------------------------------ */
/* Error codes                                                         */
/* ------------------------------------------------------------------ */

/**
 * The failures this module answers with. `code` is the contract; the Hebrew that travels beside it
 * is not — same rule as `PLANNING_ERROR_CODES` and `PH_ERROR_CODES`.
 *
 * The copy lives in `productionOrders.errors.ts` on the backend, because most of these sentences
 * are raised from more than one place.
 */
export const PRODUCTION_ORDER_ERROR_CODES = {
  ORDER_NOT_FOUND: 'PO_ORDER_NOT_FOUND',
  PROJECT_NOT_FOUND: 'PO_PROJECT_NOT_FOUND',
  SUPPLIER_NOT_FOUND: 'PO_SUPPLIER_NOT_FOUND',
  NO_ITEMS: 'PO_NO_ITEMS',
  ITEM_NOT_IN_PROJECT: 'PO_ITEM_NOT_IN_PROJECT',
  /** A line from another kit than the order's, which the order's supplier never quoted. */
  ITEM_NOT_QUOTED_BY_SUPPLIER: 'PO_ITEM_NOT_QUOTED_BY_SUPPLIER',
  FILE_NOT_IN_PROJECT: 'PO_FILE_NOT_IN_PROJECT',
  FILE_NOT_FOUND: 'PO_FILE_NOT_FOUND',
  STATUS_TRANSITION: 'PO_STATUS_TRANSITION',
  NOT_SENDABLE: 'PO_NOT_SENDABLE',
  SEND_FAILED: 'PO_SEND_FAILED',
  MAIL_NOT_CONFIGURED: 'PO_MAIL_NOT_CONFIGURED',
  NOT_DELETABLE: 'PO_NOT_DELETABLE'
} as const;

export type ProductionOrderErrorCode = (typeof PRODUCTION_ORDER_ERROR_CODES)[keyof typeof PRODUCTION_ORDER_ERROR_CODES];
